import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import Sidebar from "@/components/Sidebar";
import axios from "axios";
import { authAxios as ax } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trophy, Clock, Phone, TrendingUp, Star, Medal, Award } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const langLabels = { en: "EN", es: "ES", fr: "FR", ht: "HT" };
const rankColors = ["#002FA7", "#0A0A0A", "#FFB800"];
const rankIcons = [Trophy, Medal, Award];

function KpiCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="border border-[#E5E7EB] bg-white p-4 flex items-center gap-3">
      <div className="w-9 h-9 flex items-center justify-center shrink-0" style={{ background: accent }}>
        <Icon size={16} className="text-white" />
      </div>
      <div>
        <p className="text-xl font-black text-[#0A0A0A] tracking-tight">{value}</p>
        <p className="text-[9px] tracking-[0.2em] uppercase text-[#9CA3AF] font-bold">{label}</p>
      </div>
    </div>
  );
}

export default function LeaderboardPage() {
  const { t } = useLanguage();
  const [leaderboard, setLeaderboard] = useState([]);
  const [selectedOp, setSelectedOp] = useState(null);
  const [kpis, setKpis] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await ax.get(`${API}/admin/leaderboard`);
      setLeaderboard(res.data);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const viewKpis = async (userId) => {
    try {
      const res = await ax.get(`${API}/admin/operators/${userId}/kpis`);
      setKpis(res.data);
      setSelectedOp(userId);
    } catch {}
  };

  return (
    <div className="flex h-screen bg-[#F8F9FA]">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] tracking-tight mb-6" data-testid="leaderboard-title">
            {t("leaderboard.title")}
          </h2>

          {/* Top 3 podium */}
          {leaderboard.length >= 1 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {leaderboard.slice(0, 3).map((op, i) => {
                const RankIcon = rankIcons[i] || Star;
                return (
                  <div
                    key={op.user_id}
                    className="border border-[#E5E7EB] bg-white p-5 relative overflow-hidden cursor-pointer hover:-translate-y-0.5 transition-transform"
                    style={{ borderTopWidth: "3px", borderTopColor: rankColors[i] || "#E5E7EB" }}
                    onClick={() => viewKpis(op.user_id)}
                    data-testid={`podium-${i + 1}`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="relative">
                        <Avatar className="h-11 w-11 rounded-none">
                          <AvatarImage src={op.picture} />
                          <AvatarFallback className="rounded-none text-white text-sm font-bold" style={{ background: rankColors[i] }}>{op.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-[10px] font-black text-white" style={{ background: rankColors[i] }}>
                          {i + 1}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#0A0A0A]">{op.name}</p>
                        <div className="flex gap-0.5">
                          {(op.languages || []).map((l) => (
                            <span key={l} className="text-[8px] font-bold uppercase px-1 bg-[#F1F3F5] text-[#4B5563]">{l}</span>
                          ))}
                        </div>
                      </div>
                      <RankIcon size={20} className="ml-auto" style={{ color: rankColors[i] }} />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center bg-[#F8F9FA] p-2">
                        <p className="text-lg font-black text-[#0A0A0A]">{op.calls_handled}</p>
                        <p className="text-[8px] uppercase text-[#9CA3AF] font-bold">{t("leaderboard.calls")}</p>
                      </div>
                      <div className="text-center bg-[#F8F9FA] p-2">
                        <p className="text-lg font-black text-[#0A0A0A]">{op.avg_satisfaction}</p>
                        <p className="text-[8px] uppercase text-[#9CA3AF] font-bold">{t("leaderboard.csat")}</p>
                      </div>
                      <div className="text-center bg-[#F8F9FA] p-2">
                        <p className="text-lg font-black text-[#0A0A0A]">{op.clock_hours}h</p>
                        <p className="text-[8px] uppercase text-[#9CA3AF] font-bold">{t("leaderboard.hours")}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Full leaderboard table */}
          <div className="border border-[#E5E7EB] bg-white" data-testid="leaderboard-table">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2 border-[#0A0A0A]/80">
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3 w-12">#</TableHead>
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">{t("common.name")}</TableHead>
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">{t("leaderboard.calls")}</TableHead>
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">{t("leaderboard.csat")}</TableHead>
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">{t("leaderboard.hours")}</TableHead>
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">{t("leaderboard.avg_duration")}</TableHead>
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">{t("leaderboard.score")}</TableHead>
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leaderboard.map((op) => (
                  <TableRow key={op.user_id} className="border-b border-[#E5E7EB] hover:bg-[#F8F9FA]" data-testid={`lb-row-${op.user_id}`}>
                    <TableCell className="py-2.5 font-black text-sm" style={{ color: rankColors[op.rank - 1] || "#0A0A0A" }}>{op.rank}</TableCell>
                    <TableCell className="py-2.5">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-7 w-7 rounded-none">
                          <AvatarImage src={op.picture} />
                          <AvatarFallback className="rounded-none bg-[#002FA7] text-white text-[10px] font-bold">{op.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-xs font-semibold">{op.name}</p>
                          <div className="flex gap-0.5">{(op.languages || []).map((l) => <span key={l} className="text-[7px] font-bold uppercase px-0.5 bg-[#F1F3F5] text-[#4B5563]">{l}</span>)}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-2.5 text-sm font-bold">{op.calls_handled}</TableCell>
                    <TableCell className="py-2.5">
                      <span className={`text-sm font-bold ${op.avg_satisfaction >= 4 ? "text-[#00C853]" : op.avg_satisfaction >= 3 ? "text-[#FFB800]" : "text-[#FF2A2A]"}`}>{op.avg_satisfaction}/5</span>
                    </TableCell>
                    <TableCell className="py-2.5 text-sm">{op.clock_hours}h</TableCell>
                    <TableCell className="py-2.5 text-sm">{Math.floor(op.avg_duration_sec / 60)}m {op.avg_duration_sec % 60}s</TableCell>
                    <TableCell className="py-2.5"><Badge className="rounded-none bg-[#002FA7]/10 text-[#002FA7] text-xs font-bold">{op.composite_score}</Badge></TableCell>
                    <TableCell className="py-2.5">
                      <Button variant="outline" size="sm" className="rounded-none h-7 text-[10px] font-bold" onClick={() => viewKpis(op.user_id)} data-testid={`kpi-btn-${op.user_id}`}>
                        {t("leaderboard.view_kpis")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* KPI Detail Dialog */}
          <Dialog open={!!selectedOp} onOpenChange={(o) => { if (!o) { setSelectedOp(null); setKpis(null); } }}>
            <DialogContent className="rounded-none max-w-lg" data-testid="kpi-dialog">
              <DialogHeader>
                <DialogTitle>{kpis?.operator?.name} - {t("leaderboard.kpi_details")}</DialogTitle>
              </DialogHeader>
              {kpis && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <KpiCard icon={Phone} label={t("leaderboard.calls")} value={kpis.kpis.calls_handled} accent="#002FA7" />
                    <KpiCard icon={Star} label={t("leaderboard.csat")} value={`${kpis.kpis.avg_satisfaction}/5`} accent="#00C853" />
                    <KpiCard icon={Clock} label={t("leaderboard.hours")} value={`${kpis.kpis.clock_hours}h`} accent="#FFB800" />
                    <KpiCard icon={TrendingUp} label={t("leaderboard.sessions")} value={kpis.kpis.total_sessions} accent="#0A0A0A" />
                  </div>
                  <div className="bg-[#F8F9FA] border border-[#E5E7EB] p-4">
                    <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-2">{t("client.sentiment_dist")}</p>
                    <div className="flex gap-4">
                      {Object.entries(kpis.kpis.sentiment_distribution || {}).map(([k, v]) => (
                        <div key={k} className="text-center">
                          <p className="text-lg font-black" style={{ color: k === "positive" ? "#00C853" : k === "negative" ? "#FF2A2A" : "#FFB800" }}>{v}</p>
                          <p className="text-[9px] uppercase text-[#9CA3AF] font-bold capitalize">{k}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
}
